/**
 * Writes for planned-work item lines, whatever the parent. `add` sends the parent pointer the server
 * needs to own the line; passing a `visitId` alongside it makes the new line an addition scoped to
 * that one attendance instead of a standing line on the plan.
 */
import { Injectable, inject } from '@angular/core';
import { ActivityEntityName, ItemDeletedResponse, PlannedItem, PlannedItemParent } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { EntityDataService } from '@workbuddy/services/entities';
import type { EmbeddedItemAddLine } from '@workbuddy/surfaces/embedded-item';
import { PLANNED_ITEM_QUERY_KEY } from '../planned.item.model';

@Injectable({ providedIn: 'root' })
export class PlannedItemService {

  private readonly entityData = inject(EntityDataService);
  private readonly queryClient = injectQueryClient();

  public async add(parent: PlannedItemParent, selections: readonly EmbeddedItemAddLine[], visitId?: string): Promise<PlannedItem[]> {

    const saver = this.entityData.getSave<PlannedItem>(ActivityEntityName.PlannedItem);
    const added: PlannedItem[] = [];
    for (const selection of selections.filter(item => !!item.name)) {
      const saved = await saver.mutateAsync({ ...selection, quantity: selection.quantity ?? 1, parent, ...(visitId ? { visitId } : {}) } as Partial<PlannedItem>);
      if (saved) added.push(saved);
    }
    await this.invalidate();
    return added;
  }

  public async update(itemId: string, patch: Partial<PlannedItem>): Promise<PlannedItem> {

    const updated = await this.entityData.getPatch<PlannedItem>(ActivityEntityName.PlannedItem).mutateAsync({ id: itemId, data: patch });
    await this.invalidate();
    return updated;
  }

  public async delete(itemId: string): Promise<ItemDeletedResponse> {

    const deleted = await this.entityData.getDelete(ActivityEntityName.PlannedItem).mutateAsync({ id: itemId }) as ItemDeletedResponse;
    await this.invalidate();
    return deleted;
  }

  private invalidate(): Promise<void> {

    return this.queryClient.invalidateQueries({ queryKey: PLANNED_ITEM_QUERY_KEY, refetchType: 'all' });
  }
}
